/**
 * Prepayment Pool Dashboard
 *
 * View of prepayment pool status for a fund including:
 * - Per-investor pool balances
 * - Transaction history with filters
 */

import { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Pagination } from '../ui/pagination';
import { Wallet, AlertCircle, Info, Eye, Search } from 'lucide-react';

import {
  usePrepaymentPoolDashboard,
  useInvestorPrepaymentHistory,
  useInvestorPoolsPaginated,
} from '../../hooks/react-query/usePrepaymentPool';
import { formatCurrency } from '@/utils/formatters';
import { InvestorHistoryContent } from './InvestorHistoryPanel';
interface PrepaymentPoolDashboardProps {
  fundId: string;
  className?: string;
}

interface TotalProps {
  amount: number;
  currency: string;
}

// The one number worth a headline, kept on the filter row rather than in a
// card of its own so it doesn't cost a band of the page
const Total = ({ amount, currency }: TotalProps) => (
  <div className="flex items-baseline gap-2">
    <span className="text-sm text-muted-foreground">Total prepayment pool</span>
    <span className="text-lg font-semibold text-foreground">
      {formatCurrency(amount, { currency })}
    </span>
  </div>
);

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export function PrepaymentPoolDashboard({
  fundId,
  className,
}: PrepaymentPoolDashboardProps) {
  const [selectedInvestor, setSelectedInvestor] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);

  // Debounce the search so typing doesn't fire a request per keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== search) {
        setSearch(trimmed);
        setCurrentPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchInput, search]);

  // React Query hooks - summary for totals
  const {
    summary,
    isLoading: summaryLoading,
    isError,
    error,
    refetch,
  } = usePrepaymentPoolDashboard(fundId);

  // Paginated investor pools from backend
  const { data: paginatedData, isLoading: poolsLoading } =
    useInvestorPoolsPaginated(
      fundId,
      currentPage,
      pageSize,
      onlyWithBalance,
      search || undefined
    );

  const { data: investorHistory } = useInvestorPrepaymentHistory(
    fundId,
    selectedInvestor ?? ''
  );

  const investorPools = paginatedData?.items ?? [];
  const totalCount = paginatedData?.total_count ?? 0;
  const isLoading = summaryLoading || poolsLoading;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleBalanceFilterChange = (value: string) => {
    setOnlyWithBalance(value === 'with_balance');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setOnlyWithBalance(true);
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(searchInput) || !onlyWithBalance;

  // Filter controls: one control-height row, no card padding around it
  const filterControls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label="Search investors"
          placeholder="Search investor..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72 h-9 pl-8"
        />
      </div>

      <Select
        value={onlyWithBalance ? 'with_balance' : 'all'}
        onValueChange={handleBalanceFilterChange}
      >
        <SelectTrigger className="w-44 h-9" aria-label="Filter by balance">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="with_balance">With balance</SelectItem>
          <SelectItem value="all">All investors</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="h-9"
        >
          Clear
        </Button>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        {filterControls}
        <div
          className="flex justify-center items-center py-12"
          data-testid="loading"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-muted-foreground">
            Loading prepayment pool data...
          </span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`space-y-6 ${className}`}>
        <Alert variant="destructive" data-testid="error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : 'Failed to load prepayment pool data'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetch()} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={`space-y-6 ${className}`}>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No prepayment pool data available for this fund.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* The section is titled by DrawdownManagementView's header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {filterControls}
        {totalCount > 0 && (
          <Total
            amount={summary.total_available_balance}
            currency={investorPools[0]?.currency ?? 'EUR'}
          />
        )}
      </div>

      {/* Investor Pool Balances */}
      <Card>
        <CardContent>
          {totalCount === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Wallet className="h-12 w-12 mx-auto mb-4 opacity-30" />
              {hasActiveFilters ? (
                <p>No prepayment pools match your filters.</p>
              ) : (
                <>
                  <p>No prepayment pools found</p>
                  <p className="text-sm">
                    Pools are created when regulatory capital calls are paid
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investorPools.map((pool) => (
                    <TableRow key={pool.investor_id}>
                      <TableCell className="font-medium">
                        {pool.investor_name}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(pool.available_balance, {
                          currency: pool.currency,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setSelectedInvestor(pool.investor_id)}
                          title="View History"
                          aria-label="View History"
                        >
                          <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalCount > pageSize && (
                <div className="mt-4">
                  <Pagination
                    current={currentPage}
                    total={totalCount}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[10, 20, 50]}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Investor History Dialog */}
      <Dialog
        open={!!selectedInvestor}
        onOpenChange={(open) => !open && setSelectedInvestor(null)}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Investor Transaction History</DialogTitle>
            <DialogDescription>
              Detailed prepayment pool activity for selected investor
            </DialogDescription>
          </DialogHeader>
          {investorHistory && (
            <InvestorHistoryContent investorHistory={investorHistory} />
          )}
        </DialogContent>
      </Dialog>

      {/* Info Panel */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Prepayment Pools:</strong> When regulatory capital calls are
          paid, funds go into investor-specific prepayment pools. These pools
          are automatically consumed first during future capital calls, ensuring
          regulatory capital doesn&apos;t impact IRR calculations until actually
          deployed for investments.
        </AlertDescription>
      </Alert>
    </div>
  );
}
